import type { ReactNode } from "react";
import { Icon } from "../app/Icon";

/** Freight lane visual: green origin pin → dashed track (miles) → amber dest pin. */
export function RouteLine({ origin, destination, miles }: { origin: string; destination: string; miles?: number | null }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="flex items-center gap-1.5 font-display text-2xl font-black tracking-tight sm:text-3xl">
        <Icon name="pin" className="h-5 w-5 text-verified" /> {origin}
      </span>
      <span className="relative mx-1 hidden min-w-[3rem] flex-1 items-center sm:flex">
        {miles ? <span className="absolute -top-4 left-1/2 -translate-x-1/2 font-mono text-xs text-muted">{miles.toLocaleString()} mi</span> : null}
        <span className="h-px flex-1 border-t border-dashed border-verified/60" />
        <span className="font-mono text-verified">››</span>
      </span>
      <span className="flex items-center gap-1.5 font-display text-2xl font-black tracking-tight sm:text-3xl">
        <Icon name="pin" className="h-5 w-5 text-accent" /> {destination}
      </span>
    </div>
  );
}

// Premium detail-page primitives (the hero-card layout): verified pill, a big
// monospace spec grid, attribute tags, an amber gated notice, and a meta footer.

/** Green trust pill. Posters are always verified (verification-gating). With a
 *  label → "COMPANY · VERIFIED ✓"; without → "VERIFIED ✓". */
export function VerifiedPill({ label }: { label?: string }) {
  return (
    <span className="inline-flex w-max items-center gap-1.5 rounded-md border border-verified/40 bg-verified-bg px-2.5 py-1 font-mono text-xs font-bold uppercase tracking-wide text-verified">
      {label ? `${label} · ` : ""}Verified ✓
    </span>
  );
}

/** Big label/value spec row (uppercase label + large mono value). Drops empties. */
export function SpecGrid({ items }: { items: [string, ReactNode][] }) {
  const rows = items.filter(([, v]) => v != null && v !== "");
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-5 border-y border-line py-5 sm:grid-cols-3 lg:grid-cols-6">
      {rows.map(([k, v]) => (
        <div key={k} className="grid gap-1">
          <span className="text-[11px] font-bold uppercase tracking-widest text-muted">{k}</span>
          <span className="truncate font-mono text-lg font-bold text-ink">{v}</span>
        </div>
      ))}
    </div>
  );
}

/** Row of uppercase mono attribute tags (equipment, endorsements, requirements). */
export function Tags({ items }: { items: (string | null | undefined)[] }) {
  const clean = items.filter(Boolean) as string[];
  if (!clean.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {clean.map((t, i) => (
        <span key={`${t}-${i}`} className="rounded-md border border-line bg-surface-2 px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-wide text-muted">
          {t.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

/** Amber left-border gated notice (e.g. "Verify to apply"). */
export function GatedNotice({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-l-4 border-accent/40 border-l-accent bg-accent/10 px-4 py-3 font-mono text-sm text-accent-strong">
      <span aria-hidden>🔒</span> {children}
    </div>
  );
}

/** Bottom meta strip (JOB ID / POSTED / …) — uppercase label + mono value. */
export function MetaFooter({ items }: { items: [string, ReactNode][] }) {
  const rows = items.filter(([, v]) => v != null && v !== "");
  if (!rows.length) return null;
  return (
    <div className="grid grid-cols-2 gap-4 rounded-card border border-line bg-surface p-4 sm:grid-cols-4">
      {rows.map(([k, v]) => (
        <div key={k} className="grid gap-1">
          <span className="text-[11px] font-bold uppercase tracking-widest text-muted">{k}</span>
          <span className="truncate font-mono text-sm text-ink">{v}</span>
        </div>
      ))}
    </div>
  );
}

/** Short id label like `JB-884512` from a uuid. */
export function shortId(prefix: string, id: string): string {
  return `${prefix}-${id.replace(/-/g, "").slice(0, 6).toUpperCase()}`;
}
